import FileKit from "@convertkit/file-kit"
import { useMemo } from "react"
import { useQuery, useInfiniteQuery } from "@tanstack/react-query"
import { objectAddIf } from "core/utils/object-add-if"
import useCurrentUser from "hooks/use-current-user"

const PAGE_LIMIT = 25

export const fileKitKeys = {
  fileKit: () => ["file-kit"],
  fileLists: () => [...fileKitKeys.fileKit(), "files"],
  fileList: filters => [...fileKitKeys.fileLists(), "files", filters],
  favorites: () => [...fileKitKeys.fileKit(), "favorites"]
}

export function useFileKit() {
  const user = useCurrentUser()

  return useMemo(
    () =>
      FileKit({
        authURL: "/filekit/sessions",
        filekitURL: process.env.FILEKIT_HOST,
        id: user.account_id
      }),
    [user.account_id]
  )
}

export function useFiles({
  favorite = false,
  allowedFileTypes,
  source,
  searchQuery,
  sortBy
}) {
  const filekit = useFileKit()

  const query = useInfiniteQuery({
    queryKey: fileKitKeys.fileList({
      favorite,
      allowedFileTypes,
      source,
      searchQuery,
      sortBy
    }),
    queryFn: async ({ pageParam }) => {
      const response = await filekit.files({
        type: allowedFileTypes,
        limit: PAGE_LIMIT,
        ...objectAddIf(searchQuery, { q: searchQuery }),
        ...objectAddIf(source, { source }),
        ...objectAddIf(favorite, { favorite: true }),
        ...objectAddIf(pageParam > 0, { offset: pageParam }),
        ...objectAddIf(sortBy, { sort: sortBy })
      })

      response.attachments.forEach(file => {
        file.dataURL = file.original_url
        file.thumbnailURL = file.original_url + "?w=400&fit=clip"
        file.emailURL = file.original_url + "?w=800&fit=max"
      })

      return response
    },
    getNextPageParam: lastPage =>
      lastPage.next_offset === -1 ? null : lastPage.next_offset,
    // It should be safe to set 5min stale time, because files
    // are unlikely to change, unless we actually make a change to them
    // (then we need to invalidate the cache anyway).
    staleTime: 5 * 60 * 1000
  })

  return query
}

export function useFavorites() {
  const filekit = useFileKit()

  const queryState = useQuery({
    queryKey: fileKitKeys.favorites(),
    queryFn: async () => {
      return filekit.allFavorites()
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true
  })

  /**
   * Returns filekit's attachment id if resource with source/sourceId was
   * already added to favorites
   */
  const getAttachmentId = ({ source, sourceId }) => {
    if (queryState.isLoading) {
      return null
    }

    return queryState.data.favorites.find(
      favorite => favorite.source === source && favorite.source_id === sourceId
    )?.id
  }

  return {
    ...queryState,
    getAttachmentId
  }
}
